import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { resolveRichText } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  EditableToken,
  RichTextContent,
  useContentToken,
  useProperty,
} from '@vibesites/runtime/react';
import { resolveBedroomOptionsLabel } from '../utils/bedroomOptions';
import { keepLocationTogether, keepPropertyNameTogether, stripWrappingParagraph } from '../utils/keepLocationTogether';
import {
  INNER_PAGE_HERO_SUBTITLE_CLASS,
  INNER_PAGE_HERO_TITLE_CLASS,
  InnerPageHeroSection,
} from './shared/InnerPageHeroSection';

const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Floor plans banner',
    },
  },
};


export function FloorPlansHero() {
  const property = useProperty();
  const bedroomOptionsLabel = useMemo(
    () =>
      resolveBedroomOptionsLabel(
        property.floorplans,
        property.property_bedrooms_range,
      ),
    [property.floorplans, property.property_bedrooms_range],
  );

  const subtitleRaw = useContentToken('floorplanshero.subtitle');
  const subtitleResolved = useMemo(() => {
    if (!subtitleRaw) return '';
    const resolveProperty = {
      ...(property as Record<string, unknown>),
      property_bedrooms_range:
        bedroomOptionsLabel ?? property.property_bedrooms_range ?? '',
    };
    let html = resolveRichText(subtitleRaw, resolveProperty as Record<string, any>);
    html = stripWrappingParagraph(html);
    html = keepPropertyNameTogether(html, property.property_name as string | null | undefined);
    return keepLocationTogether(
      html,
      property.property_address_city as string | null | undefined,
      property.property_address_state as string | null | undefined,
    );
  }, [subtitleRaw, property, bedroomOptionsLabel]);

  return (
    <InnerPageHeroSection
      bgGroupPath="floorplanshero.bg_image"
      bgImageDef={bgImageDef}
      altFallback="Floor plans banner"
    >
      <EditableToken
        path="floorplanshero.subtitle"
        type="richtext"
        as="h2"
        className={INNER_PAGE_HERO_SUBTITLE_CLASS}
      >
        <span
          data-vibesite-richtext=""
          dangerouslySetInnerHTML={{ __html: subtitleResolved }}
          style={{ display: 'contents' }}
        />
      </EditableToken>
      <RichTextContent
        path="floorplanshero.title"
        as="h1"
        className={INNER_PAGE_HERO_TITLE_CLASS}
      />
      <EditableSlotGroup
        groupPath="floorplanshero.bg_image"
        definition={bgImageDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
    </InnerPageHeroSection>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorPlansHero',
  displayName: 'Floor Plans Hero',
  tokens: {
    'floorplanshero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Floor Plans</p>',
    },
    'floorplanshero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_bedrooms_range"></span> Apartments in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>',
      description:
        'Short, SEO-style headline (e.g. "1, 2, and 3-Bedroom Apartments in Tigard, OR"). The bedrooms field token lists each option individually (e.g. "Studio, 1, 2 & 3") without bed/bedroom labels.',
    },
  },
  slotGroups: {
    'floorplanshero.bg_image': bgImageDef,
  },
};

(FloorPlansHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FloorPlansHero;
